import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Alert, Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import { EXPORT_USER_STATE } from './graphql';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ExportPanel = () => {
  const [sourceUserId, setSourceUserId] = useState('');
  const [runExport, { data, loading, error, reset }] = useMutation(EXPORT_USER_STATE);

  const sourceValid = UUID_RE.test(sourceUserId.trim());
  const result = data?.exportUserState;

  const handleExport = () => {
    reset();
    runExport({ variables: { input: { sourceUserId: sourceUserId.trim() } } });
  };

  const handleDownload = () => {
    if (!result) return;
    const blob = new Blob([result.exportJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user-state-${result.sourceAppId}-${result.sourceUserId}-${result.exportedAt.replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="mb-4">
      <Card.Header>
        <span className="font-weight-bold">
          <i className="fas fa-download mr-2" />
          Export User State
        </span>
      </Card.Header>
      <Card.Body>
        <Form>
          <Form.Group>
            <Form.Label>Source User ID</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. 11111111-2222-3333-4444-555555555555"
              className="dimmed-placeholder"
              value={sourceUserId}
              onChange={(e) => setSourceUserId(e.currentTarget.value)}
              isInvalid={sourceUserId.length > 0 && !sourceValid}
              disabled={loading}
            />
            <Form.Control.Feedback type="invalid">Must be a valid UUID.</Form.Control.Feedback>
          </Form.Group>

          <Button variant="primary" disabled={!sourceValid || loading} onClick={handleExport}>
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="mr-2" />
                Exporting...
              </>
            ) : (
              'Export'
            )}
          </Button>
        </Form>

        {error && (
          <Alert variant="danger" className="mt-3 py-2">
            {error.message}
          </Alert>
        )}

        {result && !loading && !error && (
          <>
            <Table bordered size="sm" className="mt-4">
              <tbody>
                <tr>
                  <th style={{ width: 220 }}>Source app</th>
                  <td>{result.sourceAppId}</td>
                </tr>
                <tr>
                  <th>Source user</th>
                  <td><code>{result.sourceUserId}</code></td>
                </tr>
                <tr>
                  <th>Exported at</th>
                  <td>{new Date(result.exportedAt).toLocaleString()}</td>
                </tr>
                <tr>
                  <th>Size</th>
                  <td>{(result.exportJson.length / 1024).toFixed(1)} KB</td>
                </tr>
              </tbody>
            </Table>
            <Button variant="success" onClick={handleDownload}>
              <i className="fas fa-file-download mr-2" />
              Download JSON
            </Button>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default ExportPanel;
